import { motion } from "framer-motion";
import { User, Briefcase, Building2, Plane, Upload, X } from "lucide-react";
import { useState, useRef } from "react";

export default function Step2PersonalDetails({ formData, setFormData, errors, showProfilePhoto = true }) {
  const [preview, setPreview] = useState(formData.profilePhoto ? URL.createObjectURL(formData.profilePhoto) : null);
  const fileInputRef = useRef(null);

  const handleChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert("File size must be less than 5MB");
        return;
      }
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file");
        return;
      }
      handleChange("profilePhoto", file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveFile = () => {
    handleChange("profilePhoto", null);
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3 }}
      className="p-6 bg-white rounded-xl border border-gray-200 shadow-sm sm:p-8"
    >
      <div className="flex gap-3 items-center mb-8">
        <div className="flex justify-center items-center w-10 h-10 text-white bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-md">
          <User size={20} />
        </div>
        <h2 className="text-2xl font-bold text-gray-900">Personal Details</h2>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
        <div>
          <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
            <Briefcase size={16} className="text-orange-600" />
            Profession <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.profession || ""}
            onChange={(e) => handleChange("profession", e.target.value)}
            className={`w-full px-4 py-3 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
              errors?.profession
                ? "border-red-300 bg-red-50"
                : "border-gray-300 hover:border-gray-400"
            }`}
            placeholder="Enter your profession"
          />
          {errors?.profession && (
            <p className="mt-1 text-sm text-red-500">{errors.profession}</p>
          )}
        </div>

        <div>
          <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
            <Building2 size={16} className="text-orange-600" />
            Organization/University <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.organization || ""}
            onChange={(e) => handleChange("organization", e.target.value)}
            className={`w-full px-4 py-3 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
              errors?.organization
                ? "border-red-300 bg-red-50"
                : "border-gray-300 hover:border-gray-400"
            }`}
            placeholder="Enter organization or university"
          />
          {errors?.organization && (
            <p className="mt-1 text-sm text-red-500">{errors.organization}</p>
          )}
        </div>

        <div>
          <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
            <Plane size={16} className="text-orange-600" />
            Departure City <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={formData.departureCity || ""}
            onChange={(e) => handleChange("departureCity", e.target.value)}
            className={`w-full px-4 py-3 text-gray-900 bg-white rounded-lg border transition-all outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 ${
              errors?.departureCity
                ? "border-red-300 bg-red-50"
                : "border-gray-300 hover:border-gray-400"
            }`}
            placeholder="Enter departure city"
          />
          {errors?.departureCity && (
            <p className="mt-1 text-sm text-red-500">{errors.departureCity}</p>
          )}
        </div>
      </div>

      {showProfilePhoto && (
      <div className="mt-6">
        <label className="flex gap-2 items-center mb-2 text-sm font-semibold text-gray-700">
          <Upload size={16} className="text-orange-600" />
          Upload your profile photo <span className="text-red-500">*</span>
        </label>
        <div className="flex gap-4 items-start">
          <div className="flex-1">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="hidden"
              id="profile-photo-upload"
            />
            <label
              htmlFor="profile-photo-upload"
              className="flex gap-2 justify-center items-center px-4 py-3 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg border border-gray-300 transition-all cursor-pointer hover:bg-gray-100"
            >
              <Upload size={18} />
              Choose File
            </label>
            <p className="mt-2 text-xs text-gray-500">
              {formData.profilePhoto
                ? formData.profilePhoto.name
                : "No file chosen"}
            </p>
            {errors?.profilePhoto && (
              <p className="mt-1 text-sm text-red-500">{errors.profilePhoto}</p>
            )}
          </div>
          {preview && (
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="overflow-hidden relative w-24 h-24 rounded-lg border-2 border-gray-200"
            >
              <img
                src={preview}
                alt="Profile preview"
                className="object-cover w-full h-full"
              />
              <button
                type="button"
                onClick={handleRemoveFile}
                className="absolute top-1 right-1 p-1 text-white bg-red-500 rounded-full transition-colors hover:bg-red-600"
              >
                <X size={14} />
              </button>
            </motion.div>
          )}
        </div>
      </div>
      )}
    </motion.div>
  );
}

